import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import type { TownAccess } from "../lib/town-access";

const ACCESS_STORAGE_KEY = "littlelives-town:access";
/** The office this browser last opened, kept across visits so the app goes straight back to it. */
const LAST_OFFICE_STORAGE_KEY = "littlelives-town:last-office";

export type TownAccessStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function isTownAccess(value: unknown): value is TownAccess {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<TownAccess>;
  return Boolean(
    candidate.organizationId &&
      candidate.companyName &&
      candidate.roomName &&
      candidate.identity?.id &&
      candidate.identity.name,
  );
}

function readAccess(storage: TownAccessStorage | undefined, key: string): TownAccess | null {
  if (!storage) return null;
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    const access = JSON.parse(serialized) as unknown;
    return isTownAccess(access) ? access : null;
  } catch {
    return null;
  }
}

/**
 * The office this tab is in. Each tab keeps its own in `storage`, so two tabs can sit in different offices; the last
 * one opened is also kept in `lastOffice`, so a new visit returns to it. The office route verifies it with the server
 * before entering.
 */
export class TownAccessStore {
  readonly #storage?: TownAccessStorage;
  readonly #lastOffice?: TownAccessStorage;
  readonly #listeners = new Set<() => void>();
  #snapshot: TownAccess | null;

  constructor(storage?: TownAccessStorage, lastOffice?: TownAccessStorage) {
    this.#storage = storage;
    this.#lastOffice = lastOffice;
    this.#snapshot = readAccess(storage, ACCESS_STORAGE_KEY) ?? readAccess(lastOffice, LAST_OFFICE_STORAGE_KEY);
  }

  getSnapshot = () => this.#snapshot;

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  setAccess = (access: TownAccess) => {
    this.#snapshot = access;
    const serialized = JSON.stringify(access);
    try {
      this.#storage?.setItem(ACCESS_STORAGE_KEY, serialized);
    } catch {
      // A private browser session can still keep access in memory.
    }
    try {
      this.#lastOffice?.setItem(LAST_OFFICE_STORAGE_KEY, serialized);
    } catch {
      // Without it the next visit starts from the office list.
    }
    this.#emit();
  };

  clearAccess = () => {
    this.#snapshot = null;
    try {
      this.#storage?.removeItem(ACCESS_STORAGE_KEY);
      this.#lastOffice?.removeItem(LAST_OFFICE_STORAGE_KEY);
    } catch {
      // Clearing the in-memory snapshot is enough for this tab.
    }
    this.#emit();
  };

  #emit() {
    for (const listener of this.#listeners) listener();
  }
}

function browserStorage(name: "sessionStorage" | "localStorage"): TownAccessStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window[name];
  } catch {
    return undefined;
  }
}

const browserTownAccessStore = new TownAccessStore(browserStorage("sessionStorage"), browserStorage("localStorage"));
const TownAccessStoreContext = createContext<TownAccessStore | null>(null);

export function TownAccessProvider({
  children,
  store = browserTownAccessStore,
}: {
  children: ReactNode;
  store?: TownAccessStore;
}) {
  return <TownAccessStoreContext value={store}>{children}</TownAccessStoreContext>;
}

export function useTownAccessStore(): TownAccessStore {
  const store = useContext(TownAccessStoreContext);
  if (!store) throw new Error("TownAccessProvider is missing");
  return store;
}

export function useTownAccess(): TownAccess | null {
  const store = useTownAccessStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
